import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarIcon } from './Icons';
import useClickOutside from '../hooks/useClickOutside';
import useFocusTrap from '../hooks/useFocusTrap';

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MIN_BOOKING_LEAD_DAYS = 7;
const EMPTY_SET = new Set();

const toDateString = (year, month, day) => {
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const buildMonthGrid = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array(firstDay).fill(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(day);
  }
  return cells;
};

const ARROW_OFFSET = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -7,
  ArrowDown: 7,
};

const MAX_SKIP_ATTEMPTS = 366;

const BookingCalendar = ({
  selected,
  onSelect,
  id,
  unavailableDates = EMPTY_SET,
  errorId,
  isInvalid,
}) => {
  const today = useMemo(() => new Date(), []);
  const earliestAvailable = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + MIN_BOOKING_LEAD_DAYS);
    return date;
  }, []);
  const earliestAvailableString = useMemo(
    () =>
      toDateString(
        earliestAvailable.getFullYear(),
        earliestAvailable.getMonth(),
        earliestAvailable.getDate()
      ),
    [earliestAvailable]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(earliestAvailable.getFullYear());
  const [viewMonth, setViewMonth] = useState(earliestAvailable.getMonth());
  const [activeDay, setActiveDay] = useState(earliestAvailable.getDate());
  const containerRef = useRef(null);
  const popoverRef = useRef(null);
  const dayRefs = useRef({});

  const closeCalendar = useCallback(() => setIsOpen(false), []);
  useClickOutside(containerRef, closeCalendar, { active: isOpen });
  useFocusTrap(popoverRef, isOpen, closeCalendar);

  const cells = useMemo(
    () => buildMonthGrid(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  const isDayDisabled = useCallback(
    (dateString) =>
      dateString < earliestAvailableString || unavailableDates.has(dateString),
    [earliestAvailableString, unavailableDates]
  );

  // Keep the roving-tabindex target valid whenever the visible month changes
  // (e.g. after Prev/Next month) — falls back to the first enabled day.
  // Deliberately does not depend on `activeDay`: arrow-key movement (below)
  // is responsible for keeping it on an enabled day within the same month.
  useEffect(() => {
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    setActiveDay((current) => {
      const currentDateString = toDateString(viewYear, viewMonth, current);
      const isValid =
        current >= 1 &&
        current <= daysInMonth &&
        !isDayDisabled(currentDateString);
      if (isValid) return current;

      const firstEnabled = cells.find(
        (day) =>
          day !== null && !isDayDisabled(toDateString(viewYear, viewMonth, day))
      );
      return firstEnabled ?? current;
    });
  }, [viewYear, viewMonth, cells, isDayDisabled]);

  useEffect(() => {
    if (isOpen) dayRefs.current[activeDay]?.focus();
  }, [activeDay, isOpen]);

  const isBeforeEarliestMonth =
    viewYear < earliestAvailable.getFullYear() ||
    (viewYear === earliestAvailable.getFullYear() &&
      viewMonth <= earliestAvailable.getMonth());

  const goToPrevMonth = () => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  };

  const goToNextMonth = () => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  };

  const moveActiveDayBy = (offsetDays) => {
    const step = offsetDays > 0 ? 1 : -1;
    let next = new Date(viewYear, viewMonth, activeDay + offsetDays);

    // Skip past consecutive disabled days in the direction of travel,
    // rather than landing on one (arrow keys should always land somewhere
    // selectable).
    for (let attempts = 0; attempts < MAX_SKIP_ATTEMPTS; attempts += 1) {
      const dateString = toDateString(
        next.getFullYear(),
        next.getMonth(),
        next.getDate()
      );
      if (!isDayDisabled(dateString)) break;
      next = new Date(
        next.getFullYear(),
        next.getMonth(),
        next.getDate() + step
      );
    }

    if (next.getFullYear() !== viewYear || next.getMonth() !== viewMonth) {
      setViewYear(next.getFullYear());
      setViewMonth(next.getMonth());
    }
    setActiveDay(next.getDate());
  };

  const handleGridKeyDown = (event) => {
    const offset = ARROW_OFFSET[event.key];
    if (offset === undefined) return;
    event.preventDefault();
    moveActiveDayBy(offset);
  };

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString(
    'en-GB',
    { month: 'long', year: 'numeric' }
  );

  return (
    <div className="booking-calendar" ref={containerRef}>
      <div className="form__input-wrapper">
        <CalendarIcon className="form__input-icon" size={18} />
        <button
          type="button"
          id={id}
          className="booking-calendar__trigger form__input"
          onClick={() => setIsOpen((open) => !open)}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-describedby={errorId}
          aria-invalid={isInvalid ? true : undefined}
        >
          <span
            className={
              selected
                ? 'booking-calendar__trigger-value'
                : 'booking-calendar__trigger-placeholder'
            }
          >
            {selected ? formatDisplayDate(selected) : 'Select a date'}
          </span>
        </button>
      </div>

      {isOpen && (
        <div
          className="booking-calendar__popover"
          role="dialog"
          aria-modal="true"
          aria-label={`Choose a date, ${monthLabel}`}
          ref={popoverRef}
          tabIndex={-1}
        >
          <div className="booking-calendar__header">
            <button
              type="button"
              className="booking-calendar__nav"
              onClick={goToPrevMonth}
              disabled={isBeforeEarliestMonth}
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="booking-calendar__month">{monthLabel}</span>
            <button
              type="button"
              className="booking-calendar__nav"
              onClick={goToNextMonth}
              aria-label="Next month"
            >
              ›
            </button>
          </div>

          <div className="booking-calendar__legend">
            <span className="booking-calendar__legend-item">
              <span className="booking-calendar__swatch booking-calendar__swatch--available" />
              Available
            </span>
            <span className="booking-calendar__legend-item">
              <span className="booking-calendar__swatch booking-calendar__swatch--disabled" />
              Unavailable / too soon
            </span>
            <span className="booking-calendar__legend-item">
              <span className="booking-calendar__swatch booking-calendar__swatch--today" />
              Today
            </span>
          </div>

          <div className="booking-calendar__weekdays" aria-hidden="true">
            {WEEKDAY_LABELS.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>

          <div
            className="booking-calendar__grid"
            role="group"
            aria-label={monthLabel}
            onKeyDown={handleGridKeyDown}
          >
            {cells.map((day, index) => {
              if (day === null) {
                return (
                  <span
                    key={`empty-${index}`}
                    className="booking-calendar__day booking-calendar__day--empty"
                    aria-hidden="true"
                  />
                );
              }

              const dateString = toDateString(viewYear, viewMonth, day);
              const isTooSoon = dateString < earliestAvailableString;
              const isFull = unavailableDates.has(dateString);
              const isDisabled = isDayDisabled(dateString);
              const isSelected = selected === dateString;
              const isToday =
                dateString ===
                toDateString(
                  today.getFullYear(),
                  today.getMonth(),
                  today.getDate()
                );

              return (
                <button
                  type="button"
                  key={dateString}
                  ref={(el) => {
                    dayRefs.current[day] = el;
                  }}
                  tabIndex={day === activeDay ? 0 : -1}
                  className={[
                    'booking-calendar__day',
                    isSelected && 'booking-calendar__day--selected',
                    isDisabled && 'booking-calendar__day--disabled',
                    isToday && 'booking-calendar__day--today',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={isDisabled}
                  onClick={() => {
                    onSelect(dateString);
                    setIsOpen(false);
                  }}
                  onFocus={() => setActiveDay(day)}
                  aria-pressed={isSelected}
                  aria-label={
                    isFull
                      ? `${dateString} (fully booked)`
                      : isTooSoon
                        ? `${dateString} (too soon to book)`
                        : dateString
                  }
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCalendar;
